import { ProductStructuredData, TrustpilotStructuredData } from "./types";

export const validProductStructuredData = (
  arg: unknown,
): ProductStructuredData | undefined => {
  if (!arg || typeof arg !== "object") {
    return undefined;
  }

  const obj = arg as Record<string, unknown>;

  // Check if productGroup exists and is valid
  if (!obj.productGroup || typeof obj.productGroup !== "object") {
    return undefined;
  }

  const productGroup = obj.productGroup as Record<string, unknown>;

  // Validate ProductGroup structure
  if (
    typeof productGroup["@context"] !== "string" ||
    productGroup["@type"] !== "ProductGroup" ||
    typeof productGroup.name !== "string" ||
    typeof productGroup.description !== "string" ||
    typeof productGroup.url !== "string" ||
    typeof productGroup.productGroupId !== "string" ||
    !Array.isArray(productGroup.variesBy) ||
    !Array.isArray(productGroup.hasVariant)
  ) {
    return undefined;
  }

  // Validate brand
  if (!productGroup.brand || typeof productGroup.brand !== "object") {
    return undefined;
  }
  const brand = productGroup.brand as Record<string, unknown>;
  if (brand["@type"] !== "Brand" || typeof brand.name !== "string") {
    return undefined;
  }

  // Validate variesBy array
  if (!productGroup.variesBy.every((item) => typeof item === "string")) {
    return undefined;
  }

  // Validate hasVariant array
  if (
    !productGroup.hasVariant.every((variant) => {
      if (!variant || typeof variant !== "object") return false;
      const v = variant as Record<string, unknown>;
      return (
        v["@type"] === "Product" &&
        typeof v.sku === "string" &&
        typeof v.image === "string" &&
        typeof v.name === "string" &&
        typeof v.color === "string" &&
        typeof v.size === "string" &&
        typeof v.description === "string" &&
        v.offers &&
        typeof v.offers === "object"
      );
    })
  ) {
    return undefined;
  }

  // Check if productSnippet exists and is valid
  if (!obj.productSnippet || typeof obj.productSnippet !== "object") {
    return undefined;
  }

  const productSnippet = obj.productSnippet as Record<string, unknown>;

  // Validate ProductSnippet structure
  if (
    typeof productSnippet["@context"] !== "string" ||
    productSnippet["@type"] !== "Product" ||
    typeof productSnippet.name !== "string" ||
    typeof productSnippet.sku !== "string" ||
    typeof productSnippet.image !== "string" ||
    typeof productSnippet.description !== "string" ||
    typeof productSnippet.availability !== "string" ||
    !Array.isArray(productSnippet.offers)
  ) {
    return undefined;
  }

  // Validate productSnippet brand
  if (!productSnippet.brand || typeof productSnippet.brand !== "object") {
    return undefined;
  }
  const snippetBrand = productSnippet.brand as Record<string, unknown>;
  if (
    snippetBrand["@type"] !== "Brand" ||
    typeof snippetBrand.name !== "string"
  ) {
    return undefined;
  }

  // Validate offers array
  if (
    !productSnippet.offers.every((offer) => {
      if (!offer || typeof offer !== "object") return false;
      const o = offer as Record<string, unknown>;
      return (
        o["@type"] === "Offer" &&
        typeof o.name === "string" &&
        typeof o.priceCurrency === "string" &&
        typeof o.price === "number" &&
        typeof o.itemCondition === "string" &&
        typeof o.availability === "string" &&
        typeof o.image === "string" &&
        typeof o.gtin14 === "string" &&
        typeof o.priceValidUntil === "string" &&
        typeof o.url === "string" &&
        o.seller &&
        typeof o.seller === "object"
      );
    })
  ) {
    return undefined;
  }

  // Validate optional Trustpilot data if present
  if (productSnippet.review || productSnippet.aggregateRating) {
    if (
      !validTrustpilotStructuredData({
        review: productSnippet.review,
        aggregateRating: productSnippet.aggregateRating,
      })
    ) {
      return undefined;
    }
  }

  // If all validations pass, return the validated data
  return arg as ProductStructuredData;
};

const validTrustpilotStructuredData = (
  arg: unknown,
): TrustpilotStructuredData | undefined => {
  if (!arg || typeof arg !== "object") {
    return undefined;
  }

  const obj = arg as Record<string, unknown>;

  // Check if review exists and is an array
  if (!obj.review || !Array.isArray(obj.review)) {
    return undefined;
  }

  // Validate review array
  if (
    !obj.review.every((review) => {
      if (!review || typeof review !== "object") return false;
      const r = review as Record<string, unknown>;

      if (
        r["@type"] !== "Review" ||
        typeof r.reviewBody !== "string" ||
        typeof r.datePublished !== "string" ||
        !r.author ||
        typeof r.author !== "object" ||
        !r.reviewRating ||
        typeof r.reviewRating !== "object"
      ) {
        return false;
      }

      const author = r.author as Record<string, unknown>;
      if (author["@type"] !== "Person" || typeof author.name !== "string") {
        return false;
      }

      const reviewRating = r.reviewRating as Record<string, unknown>;
      if (
        reviewRating["@type"] !== "Rating" ||
        typeof reviewRating.ratingValue !== "string"
      ) {
        return false;
      }

      return true;
    })
  ) {
    return undefined;
  }

  // Check if aggregateRating exists and is an object
  if (!obj.aggregateRating || typeof obj.aggregateRating !== "object") {
    return undefined;
  }

  const aggregateRating = obj.aggregateRating as Record<string, unknown>;

  // Validate AggregateRating structure
  if (
    aggregateRating["@type"] !== "AggregateRating" ||
    typeof aggregateRating.ratingValue !== "string" ||
    typeof aggregateRating.reviewCount !== "string"
  ) {
    return undefined;
  }

  return arg as TrustpilotStructuredData;
};
